<script setup>
/**
 * 評價內容 Modal (ReviewModal.vue)
 *
 * 顯示課程公開評價或給講師的悄悄話內容，以及六角團隊的回覆（若有）。
 *
 * @fires close - 關閉 Modal
 */
import { defineProps } from 'vue';
const props = defineProps({
  reviewData: {
    type: Object,
    default: null,
  },
  reviewType: {
    type: String,
    default: "",
  }
});
</script>

<template>
  <div class="review-modal-bg">
    <div class="review-modal">
      <div class="px-4">
        <h3 class="text-center">${props.reviewType === 'video_course_review_reply'? '您給予課程的公開評價' :  '您給予講師的悄悄話'}</h3>
        <p class="text-muted mb-1">您的留言</p>
        <div class="">${ props.reviewType === 'video_course_review_reply'? props.reviewData.content : props.reviewData.whisper.content }</div>
        <hr />
        <div v-if="props.reviewType === 'video_course_review_reply' && props.reviewData.reply?.content">
          <p class="text-muted mb-1">六角團隊的回覆</p>
          <div class="">${ props.reviewData.reply.content }</div>
        </div>
        <div v-if="props.reviewType === 'video_course_review_whisper_reply' && props.reviewData.whisper.reply?.content">
          <p class="text-muted mb-1">六角團隊的回覆</p>
          <div class="">${ props.reviewData.whisper.reply.content }</div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3 px-4">
        <button
          class="btn btn-hex-primary px-4 rounded-0"
          @click="$emit('close')"
        >
          關閉
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.review-modal-bg {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10000;
  min-height: 550px;
  background-color: rgba(0, 0, 0, 0.4);
}
.review-modal {
  background-color: white;
  position: absolute;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  width: 65%;
  max-width: 550px;
  height: auto;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  border-radius: 0.25rem;
}
</style>

<spec lang="md">
# 評價內容 Modal (ReviewModal.vue)

## 元件概述
- **用途**：顯示使用者給課程的公開評價或給講師的悄悄話，以及六角團隊的回覆內容。

## Props
| 名稱 | 類型 | 必填 | 預設值 | 說明 |
|-----|------|-----|--------|------|
| reviewData | `object` | 否 | `null` | 評價資料（含 `content` 或 `whisper` 及其 `reply`） |
| reviewType | `string` | 否 | `''` | 評價類型：`video_course_review_reply`（公開評價）或 `video_course_review_whisper_reply`（悄悄話） |

## Emits
| 事件名稱 | 參數 | 說明 |
|---------|-----|------|
| close | `()` | 點擊「關閉」時觸發 |

## Slots
無

## 功能行為
- `reviewType` 為 `video_course_review_reply` 時，標題顯示「您給予課程的公開評價」並取 `reviewData.content`
- `reviewType` 為 `video_course_review_whisper_reply` 時，標題顯示「您給予講師的悄悄話」並取 `reviewData.whisper.content`
- 有對應的團隊回覆（`reply.content`）時，額外顯示「六角團隊的回覆」區塊
- 點擊「關閉」時 emit `close`

## 測試行為
- `reviewType` 為公開評價時，應顯示公開評價標題與 `reviewData.content`
- `reviewType` 為悄悄話時，應顯示悄悄話標題與 `reviewData.whisper.content`
- 有團隊回覆內容時，應顯示「六角團隊的回覆」區塊；無則不顯示
- 點擊「關閉」時，應 emit `close`

## UI 規格
- 半透明黑色遮罩 `.review-modal-bg`（全螢幕、z-index 10000）
- 白色 Modal `.review-modal`，最大寬度 550px、置中偏上（top 100px）
- 底部靠右「關閉」按鈕（btn-hex-primary）
</spec>
